import type { Frame } from '@xiangqi/protocol';
import { api, getApiBaseUrl } from '../api.ts';

export type ConnectionStatus = 'connecting' | 'live' | 'reconnecting' | 'closed';

export interface PendingCommand {
  command_id: string;
  ply: number;
  move: { from: number; to: number };
}

interface Options {
  matchId: string;
  onFrame: (frame: Frame) => void;
  onStatus: (status: ConnectionStatus) => void;
}

const BASE_DELAY_MS = 250;
const MAX_DELAY_MS = 8_000;

function wsUrl(): string {
  const base = getApiBaseUrl();
  if (base) {
    const wsProto = base.startsWith('https:') ? 'wss:' : 'ws:';
    const host = base.replace(/^https?:\/\//, '');
    return `${wsProto}//${host}/v1/realtime/ws`;
  }
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/v1/realtime/ws`;
}

/**
 * One match socket: ticket -> auth -> subscribe(cursor) -> frames.
 * Reconnects with full jitter, a FRESH one-time ticket per attempt, and
 * resends the pending command with its original command_id (server dedupes).
 */
export class MatchSocket {
  private ws: WebSocket | null = null;
  private cursor = 0;
  private stopped = false;
  private attempts = 0;
  private pending: PendingCommand | null = null;
  private reconnectTimer: number | null = null;
  private pingInterval: number | null = null;
  private lastPongReceived = Date.now();
  private readonly opts: Options;

  private onVisibilityOrOnline = (): void => {
    if (this.stopped) return;
    const isVisible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
    if (isVisible) {
      const isDead =
        !this.ws ||
        this.ws.readyState !== WebSocket.OPEN ||
        Date.now() - this.lastPongReceived > 16_000;
      if (isDead) {
        this.attempts = 0;
        if (this.reconnectTimer !== null) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        try {
          this.ws?.close();
        } catch {
          // ignore
        }
        void this.connect(true);
      }
    }
  };

  constructor(opts: Options) {
    this.opts = opts;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onVisibilityOrOnline);
      document.addEventListener('visibilitychange', this.onVisibilityOrOnline);
    }
  }

  start(): void {
    void this.connect(false);
  }

  stop(): void {
    this.stopped = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onVisibilityOrOnline);
      document.removeEventListener('visibilitychange', this.onVisibilityOrOnline);
    }
    this.ws?.close();
    this.opts.onStatus('closed');
  }

  /** Force reconnect immediately (e.g. user clicks Reconnect button or wake up) */
  forceReconnect(): void {
    if (this.stopped) return;
    this.attempts = 0;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    try {
      this.ws?.close();
    } catch {
      // ignore
    }
    void this.connect(true);
  }

  /** Track the resume cursor from delivered frames. */
  private observe(frame: Frame): void {
    if (typeof frame.stream_seq === 'number' && frame.stream_seq > this.cursor) {
      this.cursor = frame.stream_seq;
    }
  }

  sendMove(pending: PendingCommand): void {
    this.pending = pending;
    this.sendJson({ type: 'game.move', ...pending });
  }

  ackMove(ply: number): void {
    if (this.pending && ply >= this.pending.ply + 1) this.pending = null;
  }

  clearPending(): void {
    this.pending = null;
  }

  sendJson(payload: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  private startHeartbeat(): void {
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
    }
    this.lastPongReceived = Date.now();
    this.pingInterval = window.setInterval(() => {
      if (this.stopped || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      // If no pong for > 16 seconds (2 missed cycles), connection is a dead zombie
      if (Date.now() - this.lastPongReceived > 16_000) {
        try {
          this.ws.close();
        } catch {
          // ignore
        }
        return;
      }
      this.sendJson({ type: 'ping', nonce: Date.now() });
    }, 8_000);
  }

  private async connect(isReconnect: boolean): Promise<void> {
    if (this.stopped) return;
    this.opts.onStatus(isReconnect ? 'reconnecting' : 'connecting');
    try {
      // Tickets are single-use: fetch a fresh one for every attempt.
      const { ticket } = await api.wsTicket();
      if (this.stopped) return;
      const ws = new WebSocket(wsUrl());
      this.ws = ws;

      ws.addEventListener('message', (ev) => {
        let frame: Frame;
        try {
          frame = JSON.parse(String(ev.data)) as Frame;
        } catch {
          return;
        }

        if (frame.type === 'pong') {
          this.lastPongReceived = Date.now();
          return;
        }

        if (frame.type === 'welcome') {
          this.lastPongReceived = Date.now();
          this.startHeartbeat();
          ws.send(
            JSON.stringify({
              type: 'subscribe',
              match_id: this.opts.matchId,
              ...(this.cursor > 0 ? { cursor: this.cursor } : {}),
            }),
          );
          this.attempts = 0;
          this.opts.onStatus('live');
          // Resend the un-acked command (same id — the server dedupes).
          if (this.pending) this.sendJson({ type: 'game.move', ...this.pending });
          return;
        }
        this.observe(frame);
        this.opts.onFrame(frame);
      });

      ws.addEventListener('open', () => {
        ws.send(JSON.stringify({ type: 'auth', ticket }));
      });

      ws.addEventListener('close', () => {
        if (this.ws === ws) {
          if (this.pingInterval !== null) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
          }
          this.scheduleReconnect();
        }
      });
    } catch {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    this.attempts += 1;
    // Keep retrying with jittered backoff; never permanently abandon while page is open
    const ceiling = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** Math.min(this.attempts, 8));
    const delay = Math.floor(Math.random() * ceiling);
    this.opts.onStatus('reconnecting');
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => {
      void this.connect(true);
    }, delay);
  }
}
